import { effectScope, onMounted, onScopeDispose, watch } from 'vue';
import type { WatchSource } from 'vue';
import { useScrollLock } from '@vueuse/core';

// TODO(frontkit 1.0): во фронткит 1.0 есть директива v-scroll-lock
// (runtime/plugins/scroll-lock.client.ts), но там на каждый элемент свой
// useScrollLock и один общий previousPaddingRight, то есть баг со счётчиком,
// который чинится здесь. Пока фикс не приедет во фронткит, остаётся этот файл.

let sharedLock: ReturnType<typeof useScrollLock> | null = null;
let lockCount = 0;
let previousPaddingRight: string | undefined;

function getSharedLock() {
  if (!sharedLock) {
    // Отдельный scope: watch внутри useScrollLock должен пережить компонент,
    // который первым попросил замок.
    effectScope(true).run(() => {
      sharedLock = useScrollLock(document.body);
    });
  }

  return sharedLock as ReturnType<typeof useScrollLock>;
}

function reserveScrollBarGap(locked: boolean) {
  if (locked) {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0) {
      previousPaddingRight = document.body.style.paddingRight;
      const computedPadding =
        Number.parseFloat(
          window.getComputedStyle(document.body).paddingRight,
        ) || 0;
      document.body.style.paddingRight = `${computedPadding + gap}px`;
    }
  } else if (previousPaddingRight !== undefined) {
    document.body.style.paddingRight = previousPaddingRight;
    previousPaddingRight = undefined;
  }
}

function applyLock() {
  const locked = lockCount > 0;
  const lock = getSharedLock();
  if (lock.value === locked) return;

  reserveScrollBarGap(locked);
  lock.value = locked;
}

export default function useBodyScrollLock(source: WatchSource<boolean>) {
  let held = false;

  const setLocked = (value: unknown) => {
    const next = Boolean(value);
    if (next === held) return;

    held = next;
    lockCount = Math.max(0, lockCount + (next ? 1 : -1));
    applyLock();
  };

  // На сервере document нет, поэтому следим только после монтирования.
  onMounted(() => {
    watch(source, setLocked, { immediate: true });
  });

  // Отпустить замок, если компонент размонтировали открытым.
  onScopeDispose(() => setLocked(false));
}
